import { useCallback, useEffect, useState } from "react";
import { Grid, Typography } from "@mui/material";
import { Sync } from "@mui/icons-material";
import { AutocompleteInput, Button, ReferenceInput, required, TextInput, useNotify, useRefresh } from "react-admin";
import { useWatch } from "react-hook-form";
import { studentRequests } from "api/studentApi";
import { settings } from "app";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import FormModal from "server/components/Modals/FormModal";
import dataProvider from "server/dataProvider";
import { useNotifyError } from "utils/useNotifyError";
import CurriculumTransferPreview from "./CurriculumTransferPreview";

const CurriculumTransferFields = ({ record, onPreviewChange }) => {
  const notifyError = useNotifyError();
  const newCurriculumId = useWatch({ name: "newCurriculumId" });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  // The preview gates the submit button, so a failed load must notify and reset it.
  const loadPreview = useCallback(async () => {
    if (!newCurriculumId || newCurriculumId === record?.curriculumId) {
      setPreview(null);
      onPreviewChange(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await dataProvider.customRequest(
        studentRequests.getStudentCurriculumTransferPreview(record?.id, { newCurriculumId }),
        null,
        "GET",
      );
      setPreview(data ?? null);
      onPreviewChange(!!data);
    } catch (err) {
      setPreview(null);
      onPreviewChange(false);
      notifyError(err);
    } finally {
      setLoading(false);
    }
  }, [record?.id, record?.curriculumId, newCurriculumId, notifyError, onPreviewChange]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  return (
    <Grid
      container
      spacing={2}
    >
      <Grid
        item
        xs={12}
      >
        <Typography variant="body2">
          {`Текущ учебен план: `}
          <strong>{record?.curriculumName ?? "-"}</strong>
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
      >
        <ReferenceInput
          source="newCurriculumId"
          reference={studentRequests.getCurriculumsSelect()}
          filter={{
            specialityId: record?.specialityId || null,
            excludeIds: record?.curriculumId ? [record.curriculumId] : [],
          }}
        >
          <AutocompleteInput
            optionText="name"
            label="Нов учебен план"
            fullWidth
            validate={[
              required(),
              value => (value && value === record?.curriculumId ? "Студентът вече учи по този учебен план" : undefined),
            ]}
          />
        </ReferenceInput>
      </Grid>
      <Grid
        item
        xs={12}
      >
        {loading ? <Typography variant="body2">Зареждане на преглед...</Typography> : null}
        {!loading && <CurriculumTransferPreview preview={preview} />}
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
      >
        <TextInput
          label="Номер на заповед за прехвърляне"
          source="orderNumber"
          validate={required()}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
      >
        <CustomDateInput
          label="Дата на заповед за прехвърляне"
          source="orderDate"
          validate={required()}
          noTime
          required={true}
        />
      </Grid>
      <Grid
        item
        xs={12}
      >
        {/* Not `note` — the surrounding RecordContext would prefill it with the student's own note */}
        <TextInput
          source="transferNote"
          label="Бележка"
          sx={settings.style.textAria}
          variant="outlined"
          fullWidth
          multiline
        />
      </Grid>
    </Grid>
  );
};

const ChangeCurriculumModal = ({ record }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [previewLoaded, setPreviewLoaded] = useState(false);

  const handleSent = async data => {
    const payload = {
      newCurriculumId: data.newCurriculumId,
      orderNumber: data.orderNumber,
      orderDate: data.orderDate,
      note: data.transferNote,
    };
    await dataProvider.customRequest(studentRequests.transferStudentCurriculum(record?.id), payload, "PUT");
    refresh();
    notify("Успешно прехвърляне в нов учебен план", { type: "success" });
  };

  return (
    <FormModal
      titleContent="Прехвърляне в друг учебен план"
      dialogSize="md"
      onConfirm={handleSent}
      confirmEnabled={previewLoaded}
      resetProps={() => setPreviewLoaded(false)}
      triggerButton={
        <Button
          label="Прехвърляне в друг учебен план"
          variant="contained"
          startIcon={<Sync />}
        />
      }
    >
      <CurriculumTransferFields
        record={record}
        onPreviewChange={setPreviewLoaded}
      />
    </FormModal>
  );
};

export default ChangeCurriculumModal;
